import type { Query, QueryClient } from "@tanstack/query-core";
import type { OfflineState } from "./machine.ts";

/**
 * What a recovery reads again (2026-09-21, by the owner's decision, after a reader lost several
 * pages of a feed to a connection blip).
 *
 * The reason a recovery reads anything is that time passed in which this client could not see the
 * server change: `contracts/client-api.md` § Offline detection asks for every active query to be read
 * exactly once after the queued writes have landed. What that asked for, and what
 * `invalidateQueries({ type: "active" })` does, differ in one place, and it is the place a reader
 * notices.
 *
 * **An infinite query is not refetched.** Refetching one re-reads every page it has loaded, in order,
 * deriving each cursor from the page before it; when the refetched first page no longer answers a
 * next cursor the list collapses to that one page, and everything the reader scrolled is gone. A feed
 * is newest-first and its first page's cursor moves whenever entries arrive or leave, so this is not
 * a rare shape, which is why it read as flaky rather than as a bug. The loaded pages are marked stale
 * instead, without a refetch: they stay on screen, and the next mount or the reader's own next action
 * reads them again. Every other active query holds one payload with no pages to lose, so it is
 * invalidated and read at once, which is what the contract asked for.
 */
export function isInfiniteQuery(query: Query): boolean {
  return typeof (query.options as { getNextPageParam?: unknown }).getNextPageParam === "function";
}

/** Read every active query again, except that a paged list is marked stale rather than re-read. */
export async function refreshActive(client: QueryClient): Promise<void> {
  await client.invalidateQueries({ type: "active", predicate: (query) => !isInfiniteQuery(query) });
  await client.invalidateQueries({ type: "active", refetchType: "none", predicate: isInfiniteQuery });
}

/**
 * The refresh a recovery runs. **A degraded recovery reads nothing at all.** `degraded` is one signal,
 * not two: the cache was never taken offline, no read was paused, and nothing was missed that the
 * ordinary staleness rules do not already cover. Only a recovery from `offline`, where reads really
 * did stop, has a gap to close.
 */
export async function refreshAfterRecovery(client: QueryClient, from: OfflineState): Promise<void> {
  if (from === "offline") await refreshActive(client);
}
